import { SeoHead } from '@/components/SeoHead';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { useTranslation } from '@/i18n/i18n';
import { AiAccountingLink } from '@/components/AiAccountingLink';
import { AI_ACCOUNTING_ANCHORS } from '@/content/ai-accounting-anchors';

import { UrgencyBanner } from '@/components/dowod/UrgencyBanner';
import { ReadWithAI } from '@/components/ReadWithAI';
import { KSeFPainPoints } from '@/components/ksef/KSeFPainPoints';
import { KSeFFeatures } from '@/components/ksef/KSeFFeatures';
import { KSeFHowItWorks } from '@/components/ksef/KSeFHowItWorks';
import { KSeFPageFAQ } from '@/components/ksef/KSeFPageFAQ';

const KsefAutomatycznie = () => {
  const signupUrl = useSignupUrl({ source: 'ksef_hero' });
  const ctaSignupUrl = useSignupUrl({ source: 'ksef_cta' });
  const { t, locale } = useTranslation();
  const localizedPath = useLocalizedPath();
  const ksefPath = localizedPath('/ksef-automatycznie');
  const ksefUrl = `https://www.bookeper.pl${ksefPath}`;

  const contentLinks = [
    { href: '/blog/pillar/ksef-i-e-faktury', label: t('ksef.content.link1') },
    { href: localizedPath('/blog/ksef-2026-obowiazek-e-faktura'), label: t('ksef.content.link2') },
    {
      href: localizedPath('/blog/ksef-jak-uzyskac-dostep-i-token-2026'),
      label: t('ksef.content.link3'),
    },
  ];

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <SeoHead>
        <html lang={locale} />
        <title>{t('ksef.seo.title')}</title>
        <meta name="description" content={t('ksef.seo.description')} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={ksefUrl} />
        <meta property="og:title" content={t('ksef.seo.title')} />
        <meta property="og:description" content={t('ksef.seo.description')} />
        <meta property="og:url" content={ksefUrl} />
        <meta property="og:type" content="website" />
        <link rel="alternate" hrefLang="pl" href="https://www.bookeper.pl/ksef-automatycznie" />
        <link rel="alternate" hrefLang="en" href="https://www.bookeper.pl/en/ksef-automatycznie" />
        <link rel="alternate" hrefLang="uk" href="https://www.bookeper.pl/uk/ksef-automatycznie" />
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://www.bookeper.pl/ksef-automatycznie"
        />
      </SeoHead>

      <Navigation />

      {/* Hero Section */}
      <section
        aria-label={t('ksef.seo.title')}
        className="relative flex flex-col items-center overflow-hidden bg-background"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 size-96 bg-primary/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 size-80 bg-primary/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-24 z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.2] mb-6">
            {t('ksef.hero.title')
              .split('<primary>')
              .map((part, i) => {
                if (i === 0) return part;
                const [primary, rest] = part.split('</primary>');
                return (
                  <span key={primary}>
                    <span className="text-primary">{primary}</span>
                    {rest}
                  </span>
                );
              })}
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10">
            {t('ksef.hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-base font-bold px-8 h-14 rounded-xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300"
              asChild
            >
              <a href={signupUrl}>{t('ksef.hero.cta')}</a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-base h-14 rounded-xl border-2 border-foreground/10 hover:bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
              asChild
            >
              <a href="#jak-to-dziala">{t('ksef.hero.seeHow')}</a>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {[
              t('ksef.hero.noCreditCard'),
              t('ksef.hero.noCommitment'),
              t('ksef.hero.setupTime'),
            ].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5">
                <Check className="size-4 text-primary" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Statutory-deadline banner (BOO-3240 U3) — this page's subject IS the
          deadline, so it sits directly under the hero here. */}
      <section aria-label={t('urgency.kicker')} className="px-6 md:px-8 py-8 bg-background">
        <div className="max-w-4xl mx-auto">
          <UrgencyBanner />
        </div>
      </section>
      <ReadWithAI page="ksef" />

      <KSeFPainPoints />
      <KSeFFeatures />
      <KSeFHowItWorks />
      <KSeFPageFAQ />

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-8 bg-[hsl(var(--cta-bg))] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 size-96 bg-primary/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 size-80 bg-primary/10 rounded-full blur-[80px]" />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-6 tracking-tight text-[hsl(var(--cta-fg))]">
            {t('ksef.cta.heading')
              .split('<primary>')
              .map((part, i) => {
                if (i === 0) return part;
                const [primary, rest] = part.split('</primary>');
                return (
                  <span key={primary}>
                    <span className="text-primary">{primary}</span>
                    {rest}
                  </span>
                );
              })}
          </h2>

          <p className="text-lg md:text-xl text-[hsl(var(--cta-fg)/0.6)] mb-10 max-w-2xl mx-auto">
            {t('ksef.cta.subheading')}
          </p>

          <Button
            size="lg"
            className="text-base font-bold px-10 h-14 rounded-xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300"
            asChild
          >
            <a href={ctaSignupUrl}>{t('ksef.cta.button')}</a>
          </Button>

          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[hsl(var(--cta-fg)/0.5)]">
            {[
              t('ksef.hero.setupTime'),
              t('ksef.hero.noCreditCard'),
              t('ksef.hero.noCommitment'),
            ].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5">
                <Check className="size-4 text-primary" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Content links section */}
      <section aria-labelledby="ksef-content-heading" className="py-16 px-6 md:px-8 bg-background">
        <div className="max-w-3xl mx-auto">
          <h2
            id="ksef-content-heading"
            className="text-2xl md:text-3xl font-semibold mb-8 text-center"
          >
            {t('ksef.content.heading')}
          </h2>

          <div className="space-y-4">
            {contentLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="group flex items-center justify-between p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
              >
                <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                  {link.label}
                </span>
                <ArrowRight className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-4" />
              </a>
            ))}
          </div>
          {locale === 'pl' ? (
            <p className="mt-6 text-sm text-muted-foreground">
              Automatyzacja KSeF to część większego podziału pracy:{' '}
              <AiAccountingLink anchor={AI_ACCOUNTING_ANCHORS.ksef} />.
            </p>
          ) : null}
        </div>
      </section>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default KsefAutomatycznie;
